import { linkOptions, type LinkOptions } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import Icon from "@/utils/icons";

export enum NavigationItemType {
  NavigationGroup = "navigationGroup",
  NavigationItem = "navigationItem",
}
export interface NavigationItem {
  type: NavigationItemType.NavigationItem;
  title: string;
  icon: ReactNode;
  to: LinkOptions;
}

export interface NavigationGroup {
  type: NavigationItemType.NavigationGroup;
  title: string;
  icon: ReactNode;
  items: NavigationItem[];
}

export type NavigationCollection = (NavigationItem | NavigationGroup)[];

export function isNavigationGroup(item: NavigationItem | NavigationGroup): item is NavigationGroup {
  return item.type === NavigationItemType.NavigationGroup;
}

export function useNavigationElementsWorkspace(
  workspace_id?: string,
  deployment_id?: string,
  canManageChildWorkspaces?: boolean,
): NavigationCollection {
  const { t } = useTranslation();
  if (!workspace_id) return [];
  return [
    {
      type: NavigationItemType.NavigationItem,
      icon: <Icon.DAILY_OVERVIEW />,
      title: t("navigation.daily-overview"),
      to: linkOptions({
        to: "/workspaces/$workspace_id",
        params: {
          workspace_id,
        },
        activeOptions: { exact: true, includeSearch: false },
      }),
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.workspace"),
      icon: <Icon.WORKSPACE />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.workspace-settings"),
          icon: <Icon.SETTINGS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/details",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.workspace-users"),
          icon: <Icon.USERS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/settings/users",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.deployments"),
      icon: <Icon.DEPLOYMENTS />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.deployments-overview"),
          icon: <Icon.DEPLOYMENT />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/deployments",
            params: {
              workspace_id,
            },
            activeOptions: { exact: true, includeSearch: false },
          }),
        },
        ...(deployment_id
          ? ([
              {
                type: NavigationItemType.NavigationItem,
                title: t("navigation.deployments-settings"),
                icon: <Icon.SETTINGS />,
                to: linkOptions({
                  to: "/workspaces/$workspace_id/deployments/$deployment_id/settings",
                  params: {
                    workspace_id,
                    deployment_id,
                  },
                }),
              },
              {
                type: NavigationItemType.NavigationItem,
                title: t("navigation.deployments-customer-portal"),
                icon: <Icon.PUBLIC_PORTAL />,
                to: linkOptions({
                  to: "/workspaces/$workspace_id/deployments/$deployment_id/customer-portal",
                  params: {
                    workspace_id,
                    deployment_id,
                  },
                }),
              },
              {
                type: NavigationItemType.NavigationItem,
                title: t("navigation.deployments-users"),
                icon: <Icon.USERS />,
                to: linkOptions({
                  to: "/workspaces/$workspace_id/deployments/$deployment_id/users",
                  params: {
                    workspace_id,
                    deployment_id,
                  },
                }),
              },
            ] as NavigationItem[])
          : []),
      ],
    },
    ...(canManageChildWorkspaces
      ? ([
          {
            type: NavigationItemType.NavigationItem,
            title: t("navigation.customer-workspaces"),
            icon: <Icon.CUSTOMER_WORKSPACES />,
            to: linkOptions({
              to: "/workspaces/$workspace_id/customer-workspaces",
              params: {
                workspace_id,
              },
            }),
          },
        ] as NavigationCollection)
      : []),
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.knowledge-hub"),
      icon: <Icon.KNOWLEDGE_HUB />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.knowledge-hub-files"),
          icon: <Icon.FILES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/knowledge-hub/files",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.knowledge-hub-checklist-templates"),
          icon: <Icon.CHECKLIST_TEMPLATES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/knowledge-hub/checklist-templates",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.knowledge-hub-case-templates"),
          icon: <Icon.CASE_TEMPLATES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/knowledge-hub/case-templates",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.tags"),
      icon: <Icon.TAGS />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.tags-device-tags"),
          icon: <Icon.DEVICE_TAGS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/tags/device-tags",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.tags-maintenance-tags"),
          icon: <Icon.MAINTENANCE_TAGS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/tags/maintenance-tags",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.tags-case-tags"),
          icon: <Icon.CASE_TAGS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/tags/case-tags",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.custom-inventory"),
      icon: <Icon.MANUFACTURER />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.custom-inventory-devices"),
          icon: <Icon.DEVICES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/custom-inventory/devices",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.custom-inventory-parts"),
          icon: <Icon.PARTS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/custom-inventory/parts",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
  ];
}

export function useNavigationElementsDeployment(workspace_id?: string): NavigationCollection {
  const { t } = useTranslation();
  if (!workspace_id) return [];
  return [
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.service-jobs"),
      icon: <Icon.SERVICE_JOBS />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.service-jobs-planner"),
          icon: <Icon.SERVICE_JOB />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/service-jobs/planner",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.service-jobs-insights"),
          icon: <Icon.INSIGHTS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/service-jobs/insights",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.cases"),
      icon: <Icon.CASES />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.cases-planner"),
          icon: <Icon.CASES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/cases/planner",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.cases-insights"),
          icon: <Icon.INSIGHTS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/cases/insights",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.maintenances"),
      icon: <Icon.MAINTENANCES />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.maintenances-planner"),
          icon: <Icon.MAINTENANCES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/maintenances/planner",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.maintenances-insights"),
          icon: <Icon.INSIGHTS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/maintenances/insights",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.maintenance-strategies"),
      icon: <Icon.MAINTENANCE_STRATEGY />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/maintenance-strategies",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationItem,
      title: t("navigation.devices"),
      icon: <Icon.DEVICE />,
      to: linkOptions({
        to: "/workspaces/$workspace_id/devices",
        params: {
          workspace_id,
        },
      }),
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.device-data"),
      icon: <Icon.INSIGHTS />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.device-data-errors"),
          icon: <Icon.ERROR />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/device-data/errors",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.device-data-missions"),
          icon: <Icon.MISSION />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/device-data/missions",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.device-data-charts"),
          icon: <Icon.CHART_BAR />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/device-data/charts",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.device-data-triggers"),
          icon: <Icon.DEVICE_DATA_TRIGGER />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/device-data/triggers",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.device-data-availability"),
          icon: <Icon.AVAILABILITY />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/device-data/availability",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
    {
      type: NavigationItemType.NavigationGroup,
      title: t("navigation.inventory"),
      icon: <Icon.PART />,
      items: [
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-incoming"),
          icon: <Icon.INVENTORY_INCOMING />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/incoming",
            params: {
              workspace_id,
            },
          }),
        },

        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-inventory"),
          icon: <Icon.INVENTORY />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/parts",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-assembled"),
          icon: <Icon.PART_ASSEMBLY />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/assembled",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-picking"),
          icon: <Icon.INVENTORY_PICKING />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/picking",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-activities"),
          icon: <Icon.INVENTORY_ACTIVITIES />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/activities",
            params: {
              workspace_id,
            },
          }),
        },
        {
          type: NavigationItemType.NavigationItem,
          title: t("navigation.inventory-constraints"),
          icon: <Icon.CONSTRAINTS />,
          to: linkOptions({
            to: "/workspaces/$workspace_id/inventory/constraints",
            params: {
              workspace_id,
            },
          }),
        },
      ],
    },
  ];
}
